import { Link } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { LanguageMenu } from "@/components/language-menu";

const LBL: Record<string, { en: string; pt: string; es: string }> = {
  "Works": {en:"Works",pt:"Trabalhos",es:"Trabajos"},
  "Bio": {en:"Bio",pt:"Bio",es:"Bio"},
  "Contact": {en:"Contact",pt:"Contato",es:"Contacto"},
  "Sound": {en:"Sound",pt:"Som",es:"Sonido"},
  "Photo": {en:"Photo",pt:"Foto",es:"Foto"},
  "Performance": {en:"Performance",pt:"Performance",es:"Performance"},
  "Science": {en:"Science",pt:"Ciência",es:"Ciencia"},
  "Write": {en:"Write",pt:"Escrita",es:"Escritura"},
  "Clipping": {en:"Clipping",pt:"Clipping",es:"Prensa"},
  "CV": {en:"CV",pt:"CV",es:"CV"},
  "Menu": {en:"Menu",pt:"Menu",es:"Menú"},
  "Elsewhere": {en:"Elsewhere",pt:"Em outros lugares",es:"En otros lugares"},
  "Themes": {en:"Themes",pt:"Temas",es:"Temas"},
  "Decolonial Art": {en:"Decolonial Art",pt:"Arte decolonial",es:"Arte decolonial"},
};

const socials = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/luizajesusdoprado",
    icon: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
      </>
    ),
  },
  {
    label: "Twitter",
    href: "https://twitter.com/luizajdoprado",
    icon: <path d="M4 4l7.5 9.5L4.5 20M20 4l-7.4 8.2L20 20h-4l-9-11.6" />,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/luizajesusdoprado",
    icon: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path d="M7.5 10.5V17M7.5 7.4v.1M11.5 17v-3.6a2.1 2.1 0 014.2 0V17" />
      </>
    ),
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/luizajesusdoprado",
    icon: <path d="M14.5 8.5h2M14.5 21V9.8A2.3 2.3 0 0116.8 7.5h1.2M11 13h5.5" />,
  },
];

const elsewhere = [
  { label: "Sound", href: "/sound" },
  { label: "Write", href: "/write" },
  { label: "Performance", href: "/performance" },
  { label: "Science", href: "/science" },
];

const colCls = "space-y-1 font-sans text-[11px] font-light text-bone/70";
const titleCls = "font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-bone";

function FooterLangs() {
  return <LanguageMenu direction="up" bordered />;
}

function SocialIcons({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {socials.map((s) => (
        <a
          key={s.label}
          href={s.href}
          target="_blank"
          rel="noreferrer"
          aria-label={s.label}
          className="inline-flex h-11 w-11 items-center justify-center text-bone/70 transition-colors hover:text-bone"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {s.icon}
          </svg>
        </a>
      ))}
    </div>
  );
}

function ThemeLinks() {
  const { tx } = useI18n();
  return (
    <>
      <Link to="/themes/$slug" params={{ slug: "decolonial-art" }} className="block py-1.5 hover:text-bone">{tx({en:"Decolonial Art",pt:"Arte decolonial",es:"Arte decolonial"})}</Link>
      <Link to="/themes/$slug" params={{ slug: "art-science-technology" }} className="block py-1.5 hover:text-bone">{tx({en:"Art, Science & Technology",pt:"Arte, ciência e tecnologia",es:"Arte, ciencia y tecnología"})}</Link>
      <Link to="/themes/$slug" params={{ slug: "decolonization-mental-health" }} className="block py-1.5 hover:text-bone">{tx({en:"Decolonization & Mental Health",pt:"Descolonização e saúde mental",es:"Descolonización y salud mental"})}</Link>
      <Link to="/themes/$slug" params={{ slug: "sexuality-gender" }} className="block py-1.5 hover:text-bone">{tx({en:"Sexuality & Gender",pt:"Sexualidade e gênero",es:"Sexualidad y género"})}</Link>
    </>
  );
}

export function SiteFooter() {
  const { tx } = useI18n();
  return (
    <footer className="border-t border-bone/10 bg-ink text-bone">
      <div className="mx-auto max-w-[1800px] px-6 py-14 md:px-12 md:py-20">
        {/* ---------- mobile ---------- */}
        <div className="md:hidden">
          <div className="grid grid-cols-2 gap-8">
            <div className={colCls}>
              <p className={titleCls}>{tx({en:"Menu",pt:"Menu",es:"Menú"})}</p>
              <Link to="/works" className="block py-1.5 hover:text-bone">
                {tx({en:"Works",pt:"Trabalhos",es:"Trabajos"})}
              </Link>
              <Link to="/about" className="block py-1.5 hover:text-bone">
                {tx({en:"Bio",pt:"Bio",es:"Bio"})}
              </Link>
              <Link to="/contact" className="block py-1.5 hover:text-bone">
                {tx({en:"Contact",pt:"Contato",es:"Contacto"})}
              </Link>
              <Link to="/cv" className="block py-1.5 hover:text-bone">CV</Link>
              <Link to="/clipping" className="block py-1.5 hover:text-bone">
                {tx({en:"Clipping",pt:"Clipping",es:"Prensa"})}
              </Link>
            </div>

            <div className={colCls}>
            <p className={titleCls}>{tx({en:"Elsewhere",pt:"Em outros lugares",es:"En otros lugares"})}</p>
            <Link to="/photo" className="block py-1.5 hover:text-bone">
              {tx({en:"Photo",pt:"Foto",es:"Foto"})}
            </Link>
            {elsewhere.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="block py-1.5 hover:text-bone"
              >
                {tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
              </a>
            ))}
            </div>
          </div>

          <div className={`${colCls} mt-10 text-center`}>
            <p className={titleCls}>{tx({en:"Themes",pt:"Temas",es:"Temas"})}</p>
            <ThemeLinks />
          </div>

          <div className="mt-10 space-y-1 text-center font-sans text-[11px] font-light text-bone/70">
            <a href="mailto:luizajesusdoprado@gmail.com" className="block py-1.5 hover:text-bone">
              luizajesusdoprado@gmail.com
            </a>
            <a href="https://www.oculto.management/artists/luiza-jesus-do-prado" target="_blank" rel="noreferrer" className="block py-1.5 hover:text-bone">
              Oculto
            </a>
            <p>{tx({en:"Based in São Paulo, Berlin & Madrid",pt:"Entre São Paulo, Berlim e Madri",es:"Entre São Paulo, Berlín y Madrid"})}</p>
            <p>{tx({en:"Works worldwide",pt:"Trabalhos no mundo todo",es:"Trabajos en todo el mundo"})}</p>
          </div>
        </div>

        {/* ---------- desktop ---------- */}
        <div className="hidden gap-10 md:grid md:grid-cols-4">
          <div className={colCls}>
            <p className={titleCls}>{tx({en:"Contact",pt:"Contato",es:"Contacto"})}</p>
            <a href="mailto:luizajesusdoprado@gmail.com" className="block py-1.5 hover:text-bone">
              luizajesusdoprado@gmail.com
            </a>
            <a href="https://www.oculto.management/artists/luiza-jesus-do-prado" target="_blank" rel="noreferrer" className="block py-1.5 hover:text-bone">
              Oculto
            </a>
            <p>{tx({en:"Based in São Paulo, Berlin & Madrid",pt:"Entre São Paulo, Berlim e Madri",es:"Entre São Paulo, Berlín y Madrid"})}</p>
            <p>{tx({en:"Works worldwide",pt:"Trabalhos no mundo todo",es:"Trabajos en todo el mundo"})}</p>
          </div>

          <div className={colCls}>
            <p className={titleCls}>{tx({en:"Elsewhere",pt:"Em outros lugares",es:"En otros lugares"})}</p>
            <Link to="/photo" className="block py-1.5 hover:text-bone">
              {tx({en:"Photo",pt:"Foto",es:"Foto"})}
            </Link>
            {elsewhere.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="block py-1.5 hover:text-bone"
              >
                {tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
              </a>
            ))}
          </div>

          <div className={colCls}>
            <p className={titleCls}>{tx({en:"Themes",pt:"Temas",es:"Temas"})}</p>
            <ThemeLinks />
          </div>

          <div className={`${colCls} md:text-right`}>
            <p className={titleCls}>{tx({en:"Menu",pt:"Menu",es:"Menú"})}</p>
            <Link to="/works" className="block py-1.5 hover:text-bone">
              {tx({en:"Works",pt:"Trabalhos",es:"Trabajos"})}
            </Link>
            <Link to="/about" className="block py-1.5 hover:text-bone">
              {tx({en:"Bio",pt:"Bio",es:"Bio"})}
            </Link>
            <Link to="/contact" className="block py-1.5 hover:text-bone">
              {tx({en:"Contact",pt:"Contato",es:"Contacto"})}
            </Link>
            <Link to="/cv" className="block py-1.5 hover:text-bone">CV</Link>
            <Link to="/clipping" className="block py-1.5 hover:text-bone">
              {tx({en:"Clipping",pt:"Clipping",es:"Prensa"})}
            </Link>
          </div>
        </div>

        {/* ---------- languages + social ---------- */}
        <div className="mt-12 flex flex-col items-center justify-between gap-6 border-t border-bone/10 pt-6 md:flex-row md:items-center md:pr-16">
          <FooterLangs />
          <SocialIcons />
          <p className="font-sans text-[11px] font-light text-bone/65">
            © {new Date().getFullYear()} Luiza Jesus do Prado
          </p>
        </div>
        <div className="mt-4 flex flex-col items-center justify-between gap-3 font-sans text-[11px] font-light text-bone/65 md:flex-row md:pr-16">
          <div className="flex gap-4">
            <Link to="/terms" className="inline-block py-2 hover:text-bone">{tx({en:"Terms & Privacy",pt:"Termos e Privacidade",es:"Términos y Privacidad"})}</Link>
            <Link to="/legal-notice" className="inline-block py-2 hover:text-bone">{tx({en:"Legal notice",pt:"Aviso legal",es:"Aviso legal"})}</Link>
          </div>
          <p className="md:text-right">
            {tx({en:"Images licensed under",pt:"Imagens licenciadas sob",es:"Imágenes bajo licencia"})}{" "}
            <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="license noreferrer" className="inline-block py-2 underline underline-offset-2 hover:text-bone">CC BY-SA 4.0</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
